// Pure helpers for the ADMIN-initiated invoice adjustment (Commission tab
// "Adjust", migration 0032). No React, no Supabase, no localStorage: unit-testable.
//
// Nothing here writes anything. The Supabase-first writes live in
// src/supabase.js (createAdminAdjustment) and the orchestration in
// src/adminAdjust.js.
import { repNamesMatch, statusClass, totalFormulaConsistent } from "./invoiceAdjustments";
import { computeCorrection } from "./commissionCorrections";
import { fmtSignedMoney } from "./commissionCorrectionsView";

const norm = (s) => String(s == null ? "" : s).trim().toLowerCase();

// Same rule the rep portal uses: repCreated flag or the INV-F id prefix. A
// dealer-placed order stores discount / shipping / tier pricing only on the
// dealer's own device, so a recomputed total would be wrong: never adjustable here.
export function isRepCreatedInvoice(inv) {
  return !!inv && (!!inv.repCreated || String(inv.id || "").startsWith("INV-F"));
}

// Why the admin Adjust button must be disabled for an invoice (null = allowed).
// ctx.pendingIds: Set of invoice ids that have a pending rep adjust / void request.
// The modal re-checks everything against FRESH reads; this is the tooltip layer.
export function adminAdjustBlockReason(inv, ctx) {
  const c = ctx || {};
  if (!inv || !inv.id) return "Invoice not found.";
  if (!isRepCreatedInvoice(inv)) {
    if (inv.source === "admin_invoice") return "Invoices created in the office cannot be adjusted yet. Void it from Delete and create a new invoice.";
    return "Dealer-placed orders cannot be adjusted here (their discount and shipping are not stored on the server). Void it instead, or place a new order.";
  }
  const st = statusClass(inv.status);
  if (st === "cancelled") return "This invoice is already cancelled.";
  if (st === "paid") return "The dealer has already paid this invoice. Adjusting it would change what they paid: void it with a refund from Delete instead.";
  if (st === "payment_pending") return "A payment for this invoice is awaiting confirmation. Confirm or reject the payment in Orders first.";
  if (c.pendingIds && typeof c.pendingIds.has === "function" && c.pendingIds.has(String(inv.id))) return "The rep has a pending adjustment or void request for this invoice. Decide it in Orders first.";
  if (!totalFormulaConsistent(inv)) return "This invoice total does not match its line items, so it cannot be adjusted automatically.";
  return null;
}

// Catalog rows for the line picker: variations become first-class siblings (parents
// that HAVE variations are excluded) exactly like RepInvoices.prodsForPicker, so the
// admin sees the same SKUs and catalog prices the rep does. Kits are not offered.
export function expandCatalogForPicker(products) {
  return (Array.isArray(products) ? products : []).flatMap((p) => {
    if (!p || !p.sku) return [];
    if (Array.isArray(p.variations) && p.variations.length > 0) {
      return p.variations.filter((v) => v && v.sku).map((v) => ({
        sku: v.sku,
        name: `${p.name} — ${v.label || v.sku}`,
        price: Number(v.price) || 0,
        case: p.case,
        parentSku: p.sku,
        variationLabel: v.label || "",
      }));
    }
    return [{ sku: p.sku, name: p.name || p.sku, price: Number(p.price) || 0, case: p.case }];
  });
}

/**
 * Resolve the rep who owns an invoice to a REAL profile (auth uuid + email).
 * Order: the invoice's repId stamp, then its repEmail stamp, then the rep NAME
 * (the order row's name, then the cache's) but ONLY when it matches exactly one
 * rep profile: an exact (case-insensitive) match first, then the fuzzy matcher
 * the rep portal uses. Two candidates = ambiguous = refused (a mis-attributed
 * request would be invisible to the right rep). Never guesses.
 * profiles: rows shaped like public.profiles (id, email, full_name, role, tier).
 * Returns { ok:true, rep:{id,name,email}, via } | { ok:false, code, reason }.
 */
export function resolveRepIdentity({ invoice, dbRow, profiles } = {}) {
  const cands = (Array.isArray(profiles) ? profiles : [])
    .filter((p) => p && p.id && (norm(p.role) === "rep" || norm(p.tier) === "rep"))
    .map((p) => ({ id: String(p.id), name: String(p.full_name || p.name || p.contact || "").trim(), email: String(p.email || "").trim(), status: p.status }));
  const inv = invoice || {};
  const pick = (c, via) => ({ ok: true, rep: { id: c.id, name: c.name, email: c.email, status: c.status }, via });
  if (inv.repId != null && String(inv.repId) !== "") {
    const hit = cands.find((c) => c.id === String(inv.repId));
    if (hit) return pick(hit, "repId");
    return { ok: false, code: "unresolved", reason: `The invoice is stamped with a rep id (${inv.repId}) that is not a live rep account, so the rep cannot be identified safely.` };
  }
  if (inv.repEmail) {
    const hit = cands.filter((c) => c.email && norm(c.email) === norm(inv.repEmail));
    if (hit.length === 1) return pick(hit[0], "repEmail");
    if (hit.length > 1) return { ok: false, code: "ambiguous", reason: `More than one rep profile has the email ${inv.repEmail}.` };
    return { ok: false, code: "unresolved", reason: `No rep account has the email ${inv.repEmail} that this invoice is stamped with.` };
  }
  const names = [dbRow && dbRow.rep, inv.rep].map((x) => String(x || "").trim()).filter(Boolean);
  if (!names.length) return { ok: false, code: "unresolved", reason: "The invoice has no rep name, so the rep cannot be identified." };
  for (const nm of names) {
    const exact = cands.filter((c) => c.name && norm(c.name) === norm(nm));
    if (exact.length === 1) return pick(exact[0], "name");
    if (exact.length > 1) return { ok: false, code: "ambiguous", reason: `More than one rep is called "${nm}". Ask a developer to stamp the invoice with the rep's id before adjusting it.` };
  }
  for (const nm of names) {
    const fuzzy = cands.filter((c) => c.name && repNamesMatch(nm, c.name));
    if (fuzzy.length === 1) return pick(fuzzy[0], "fuzzy-name");
    if (fuzzy.length > 1) return { ok: false, code: "ambiguous", reason: `The rep name "${nm}" matches more than one rep account (${fuzzy.map((c) => c.name).join(", ")}). Nothing was changed.` };
  }
  return { ok: false, code: "unresolved", reason: `No rep account matches the rep name "${names[0]}" on this invoice, so the rep cannot be identified. Nothing was changed.` };
}

/**
 * The commission consequence of changing an invoice to newTotal (adjust) or
 * voiding it. Pure: paidRows / corrections are the invoice's FRESH ledger rows.
 *  - nothing paid for the invoice -> commission is derived, so it changes by itself
 *    from $A to $B (a void = to $0.00)
 *  - already paid -> a correction of +/- $X will be recorded (paid record untouched)
 * Returns { kind:'pending'|'paid'|'paid_none'|'paid_manual', from, to, delta, text }.
 */
export function commissionEffect({ paidRows, corrections, oldTotal, newTotal, rate, repName, isVoid } = {}) {
  const who = repName || "the rep";
  const r = Number(rate) || 0;
  const money = (n) => `$${(Math.round((Number(n) || 0) * 100) / 100).toFixed(2)}`;
  if (!Array.isArray(paidRows) || paidRows.length === 0) {
    const from = (Number(oldTotal) || 0) * r / 100;
    const to = isVoid ? 0 : (Number(newTotal) || 0) * r / 100;
    return { kind: "pending", from, to, delta: to - from, text: `Pending commission for ${who} changes from ${money(from)} to ${money(to)} automatically.` };
  }
  const c = computeCorrection({ paidRows, corrections: corrections || [], newTotal, isVoid: !!isVoid });
  if (c.ok && !c.none) return { kind: "paid", delta: c.delta, text: `Already paid: a correction of ${fmtSignedMoney(c.delta)} for ${who} will be recorded and netted into their next payout. The paid record itself is never changed.` };
  if (c.ok) return { kind: "paid_none", delta: 0, text: "Already paid: the paid commission already matches the new total, so no correction is needed." };
  return { kind: "paid_manual", delta: null, text: `Already paid, but the correction cannot be worked out automatically (${c.reason}). It will appear under Correction needed for a manual review.` };
}
